// src/services/dashboardService.js
import axios from "axios";
import { API } from "../utils/apiUrls";

/**
 * Build standard headers for dashboard API calls.
 */
const getHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: token,
    "Content-Type": "application/json",
  };
};

/**
 * Build the common request body for all dashboard endpoints.
 * @param {Object} opts
 * @param {string}  opts.period           - e.g. "TODAY", "LAST_30_DAYS", "CUSTOM"
 * @param {string|null} opts.fromDateTime - ISO-8601 (only for CUSTOM)
 * @param {string|null} opts.toDateTime   - ISO-8601 (only for CUSTOM)
 * @param {string|null} opts.carrierPartnerId
 * @param {string|null} opts.groupBy      - "DAY" | "WEEK" | "MONTH"
 */
const buildBody = (opts = {}) => {
  const merchantId = localStorage.getItem("merchantId");
  return {
    merchant_id: merchantId,
    period: opts.period || "TODAY",
    from_date_time: opts.fromDateTime || null,
    to_date_time: opts.toDateTime || null,
    carrier_partner_id: opts.carrierPartnerId || null,
    group_by: opts.groupBy || null,
  };
};

/**
 * Generic POST wrapper with consistent error handling.
 */
const post = async (url, opts = {}) => {
  const response = await axios.post(url, buildBody(opts), {
    headers: getHeaders(),
    timeout: 30000,
  });
  return response.data;
};

// ──────────────────────────────────────────────────
// Individual endpoint methods
// ──────────────────────────────────────────────────

export const fetchSummary = (opts) => post(API.dashboardSummary(), opts);

export const fetchShipmentsOverview = (opts) => post(API.dashboardShipmentsOverview(), opts);

export const fetchShipmentsStatus = (opts) => post(API.dashboardShipmentsStatus(), opts);

export const fetchShipmentsZoneWise = (opts) => post(API.dashboardShipmentsZoneWise(), opts);

export const fetchCourierSplit = (opts) => post(API.dashboardCourierSplit(), opts);

export const fetchDeliveryPerformance = (opts) => post(API.dashboardDeliveryPerformance(), opts);

export const fetchRevenue = (opts) => post(API.dashboardRevenue(), opts);

export const fetchNdr = (opts) => post(API.dashboardNdr(), opts);

export const fetchCod = (opts) => post(API.dashboardCod(), opts);

export const fetchAttemptAnalytics = (opts) => post(API.dashboardAttemptAnalytics(), opts);

/**
 * Fetch all dashboard data in parallel for initial page load.
 * Returns an object keyed by section name.
 */
export const fetchAllDashboardData = async (opts = {}) => {
  const [
    summary,
    shipmentsOverview,
    shipmentsStatus,
    shipmentsZoneWise,
    courierSplit,
    deliveryPerformance,
    revenue,
    ndr,
    cod,
    attemptAnalytics,
  ] = await Promise.allSettled([
    fetchSummary(opts),
    fetchShipmentsOverview(opts),
    fetchShipmentsStatus(opts),
    fetchShipmentsZoneWise(opts),
    fetchCourierSplit(opts),
    fetchDeliveryPerformance(opts),
    fetchRevenue(opts),
    fetchNdr(opts),
    fetchCod(opts),
    fetchAttemptAnalytics(opts),
  ]);

  const extract = (result) => (result.status === "fulfilled" ? result.value : null);

  return {
    summary: extract(summary),
    shipmentsOverview: extract(shipmentsOverview),
    shipmentsStatus: extract(shipmentsStatus),
    shipmentsZoneWise: extract(shipmentsZoneWise),
    courierSplit: extract(courierSplit),
    deliveryPerformance: extract(deliveryPerformance),
    revenue: extract(revenue),
    ndr: extract(ndr),
    cod: extract(cod),
    attemptAnalytics: extract(attemptAnalytics),
  };
};
